import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import type { HkdInfo, VatInvoice } from "../api";

const THIN_BORDER: Partial<ExcelJS.Borders> = {
  top: { style: "thin" },
  left: { style: "thin" },
  bottom: { style: "thin" },
  right: { style: "thin" },
};

function formatDateVi(iso: string): string {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

function setCell(
  ws: ExcelJS.Worksheet,
  row: number,
  col: number,
  value: ExcelJS.CellValue,
  opts?: { bold?: boolean; align?: Partial<ExcelJS.Alignment>; border?: boolean; numFmt?: string; italic?: boolean }
) {
  const cell = ws.getCell(row, col);
  cell.value = value;
  const font: Partial<ExcelJS.Font> = {};
  if (opts?.bold) font.bold = true;
  if (opts?.italic) font.italic = true;
  if (Object.keys(font).length) cell.font = font;
  if (opts?.align) cell.alignment = opts.align;
  if (opts?.border) cell.border = THIN_BORDER;
  if (opts?.numFmt) cell.numFmt = opts.numFmt;
  return cell;
}

/**
 * Xuất Bảng kê hóa đơn, chứng từ hàng hóa dịch vụ MUA VÀO (kê VAT đầu vào).
 */
export async function exportVatInvoices(params: {
  hkd: HkdInfo;
  invoices: VatInvoice[];
  year?: number;
  quarter?: number;
}): Promise<void> {
  const { hkd, invoices } = params;
  const now = new Date();
  const year = params.year || now.getFullYear();
  const quarter = params.quarter || Math.ceil((now.getMonth() + 1) / 3);

  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet("Bảng kê VAT đầu vào");

  // Column widths (9 cột)
  ws.getColumn(1).width = 6;   // STT
  ws.getColumn(2).width = 14;  // Số hóa đơn
  ws.getColumn(3).width = 13;  // Ngày HĐ
  ws.getColumn(4).width = 24;  // Tên người bán
  ws.getColumn(5).width = 16;  // MST người bán
  ws.getColumn(6).width = 18;  // Giá trị chưa thuế
  ws.getColumn(7).width = 12;  // Thuế suất %
  ws.getColumn(8).width = 16;  // Thuế GTGT
  ws.getColumn(9).width = 18;  // Tổng thanh toán

  let row = 1;

  // Góc phải — tên mẫu
  ws.mergeCells(row, 5, row, 9);
  setCell(ws, row, 5, "Bảng kê hóa đơn, chứng từ hàng hóa, dịch vụ mua vào", {
    bold: true,
    align: { horizontal: "center" },
  });
  row += 2;

  // Khối thông tin HKD — trái
  ws.mergeCells(row, 1, row, 5);
  setCell(ws, row, 1, `Tên hộ kinh doanh/doanh nghiệp: ${hkd.name || ""}`, { bold: true });
  row++;
  ws.mergeCells(row, 1, row, 5);
  setCell(ws, row, 1, `Mã số thuế: ${hkd.tax_code || ""}`);
  row++;
  ws.mergeCells(row, 1, row, 5);
  setCell(ws, row, 1, `Địa chỉ: ${hkd.address || ""}`);
  row += 2;

  // Tiêu đề giữa
  ws.mergeCells(row, 1, row, 9);
  setCell(ws, row, 1, "BẢNG KÊ HÓA ĐƠN, CHỨNG TỪ HÀNG HÓA, DỊCH VỤ MUA VÀO", {
    bold: true,
    align: { horizontal: "center", vertical: "middle" },
  });
  row++;

  // Kỳ
  ws.mergeCells(row, 1, row, 9);
  setCell(ws, row, 1, `Quý ${quarter} năm ${year}`, {
    italic: true,
    align: { horizontal: "center" },
  });
  row += 2;

  // Header bảng
  const headers = [
    "STT",
    "Số hóa đơn",
    "Ngày HĐ",
    "Tên người bán",
    "MST người bán",
    "Giá trị HHDV\nchưa thuế",
    "Thuế suất\n(%)",
    "Thuế GTGT",
    "Tổng thanh toán",
  ];
  for (let c = 0; c < headers.length; c++) {
    setCell(ws, row, c + 1, headers[c], {
      bold: true,
      align: { horizontal: "center", vertical: "middle", wrapText: true },
      border: true,
    });
  }
  row++;

  // Dòng số thứ tự cột
  const colNums = ["(1)", "(2)", "(3)", "(4)", "(5)", "(6)", "(7)", "(8)", "(9)"];
  for (let c = 0; c < colNums.length; c++) {
    setCell(ws, row, c + 1, colNums[c], {
      align: { horizontal: "center" },
      border: true,
      italic: true,
    });
  }
  row++;

  // Data rows
  let sumBeforeVat = 0;
  let sumVat = 0;
  let sumTotal = 0;

  for (let i = 0; i < invoices.length; i++) {
    const inv = invoices[i];
    const beforeVat = inv.amount_before_vat || 0;
    const vatAmt = inv.vat_amount || 0;
    const total = inv.total_amount || (beforeVat + vatAmt);

    setCell(ws, row, 1, i + 1, { border: true, align: { horizontal: "center" } });
    setCell(ws, row, 2, inv.invoice_number || "", { border: true, align: { horizontal: "center" } });
    setCell(ws, row, 3, formatDateVi(inv.invoice_date), { border: true, align: { horizontal: "center" } });
    setCell(ws, row, 4, inv.seller_name || inv.supplier_name || "", { border: true });
    setCell(ws, row, 5, inv.seller_tax_code || "", { border: true, align: { horizontal: "center" } });
    setCell(ws, row, 6, beforeVat, { border: true, align: { horizontal: "right" }, numFmt: "#,##0" });
    setCell(ws, row, 7, inv.vat_rate != null ? inv.vat_rate : "", { border: true, align: { horizontal: "center" } });
    setCell(ws, row, 8, vatAmt, { border: true, align: { horizontal: "right" }, numFmt: "#,##0" });
    setCell(ws, row, 9, total, { border: true, align: { horizontal: "right" }, numFmt: "#,##0" });

    sumBeforeVat += beforeVat;
    sumVat += vatAmt;
    sumTotal += total;
    row++;
  }

  // Dòng Tổng cộng
  ws.mergeCells(row, 1, row, 5);
  setCell(ws, row, 1, "Tổng cộng", { bold: true, border: true, align: { horizontal: "center" } });
  setCell(ws, row, 6, sumBeforeVat, { bold: true, border: true, align: { horizontal: "right" }, numFmt: "#,##0" });
  setCell(ws, row, 7, "", { border: true });
  setCell(ws, row, 8, sumVat, { bold: true, border: true, align: { horizontal: "right" }, numFmt: "#,##0" });
  setCell(ws, row, 9, sumTotal, { bold: true, border: true, align: { horizontal: "right" }, numFmt: "#,##0" });
  row += 3;

  // Khối ký — phải
  ws.mergeCells(row, 5, row, 9);
  setCell(ws, row, 5, `Ngày ${now.getDate()} tháng ${now.getMonth() + 1} năm ${now.getFullYear()}`, {
    italic: true,
    align: { horizontal: "center" },
  });
  row += 2;

  setCell(ws, row, 3, "NGƯỜI LẬP BẢNG KÊ", { bold: true, align: { horizontal: "center" } });
  setCell(ws, row, 7, "NGƯỜI ĐẠI DIỆN\nTHEO PHÁP LUẬT", {
    bold: true,
    align: { horizontal: "center", wrapText: true },
  });
  row++;
  setCell(ws, row, 3, "(Ký, ghi rõ họ tên)", { italic: true, align: { horizontal: "center" } });
  setCell(ws, row, 7, "(Ký, ghi rõ họ tên)", { italic: true, align: { horizontal: "center" } });
  row += 4;
  setCell(ws, row, 3, hkd.preparer?.trim() || "", { align: { horizontal: "center" } });
  setCell(ws, row, 7, hkd.owner?.trim() || "", { align: { horizontal: "center" } });

  // Save file
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const filename = `BangKe-HoaDonVAT_Q${quarter}_${year}.xlsx`;
  saveAs(blob, filename);
}
